import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ShieldCheck, X, Check } from "lucide-react";
import { cn } from "../lib/utils";

const sevMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };
const LEVEL_BG = {
  critical: "bg-red-600 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-amber-400 text-slate-900",
  low: "bg-green-500 text-white",
};

export default function RiskDetailPage() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [users, setUsers] = useState([]);
  const [showAccept, setShowAccept] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ rationale: "", acceptance_expiry: "" });
  const [edit, setEdit] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/risks/${id}`);
    setR(data);
    setEdit({
      residual_likelihood: data.residual_likelihood,
      residual_impact: data.residual_impact,
      status: data.status,
    });
  };
  useEffect(() => {
    load();
    api.get("/users?limit=200").then(({ data }) => setUsers(data.items || [])).catch(() => {});
  }, [id]);

  const accept = async (e) => {
    e.preventDefault();
    try {
      const payload = { rationale: acceptForm.rationale };
      if (acceptForm.acceptance_expiry) payload.acceptance_expiry = new Date(acceptForm.acceptance_expiry).toISOString();
      await api.post(`/risks/${id}/accept`, payload);
      toast.success("Risk accepted");
      setShowAccept(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const saveResidual = async () => {
    try {
      await api.patch(`/risks/${id}`, {
        residual_likelihood: Number(edit.residual_likelihood),
        residual_impact: Number(edit.residual_impact),
        status: edit.status,
      });
      toast.success("Updated");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  if (!r) return <div className="h-40 bg-muted animate-pulse rounded" />;

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <Link to="/risks" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Risk register
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="neutral">{r.category}</Badge>
                <Badge variant={sevMap[r.severity]}>Inherent: {r.severity}</Badge>
                <Badge variant={sevMap[r.residual_severity]}>Residual: {r.residual_severity}</Badge>
                <Badge variant={r.status === "accepted" ? "warning" : r.status === "closed" ? "neutral" : r.status === "mitigating" ? "info" : "critical"}>{r.status}</Badge>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">{r.title}</h1>
              {r.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{r.description}</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAccept(true)} data-testid="risk-accept-btn">Accept risk</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="risk-inherent-card">
          <CardHeader><CardTitle>Inherent risk</CardTitle><CardDescription>Before mitigation.</CardDescription></CardHeader>
          <CardContent>
            <ScoreDisplay likelihood={r.likelihood} impact={r.impact} score={r.inherent_score} level={r.severity} />
          </CardContent>
        </Card>
        <Card data-testid="risk-residual-card">
          <CardHeader><CardTitle>Residual risk</CardTitle><CardDescription>Adjust after applying controls.</CardDescription></CardHeader>
          <CardContent>
            {edit && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Residual likelihood: {edit.residual_likelihood}</Label>
                    <input data-testid="residual-likelihood" type="range" min="1" max="5" step="1" className="w-full mt-2"
                           value={edit.residual_likelihood} onChange={(e) => setEdit({ ...edit, residual_likelihood: Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label>Residual impact: {edit.residual_impact}</Label>
                    <input data-testid="residual-impact" type="range" min="1" max="5" step="1" className="w-full mt-2"
                           value={edit.residual_impact} onChange={(e) => setEdit({ ...edit, residual_impact: Number(e.target.value) })} />
                  </div>
                </div>
                <ScoreDisplay likelihood={edit.residual_likelihood} impact={edit.residual_impact}
                              score={edit.residual_likelihood * edit.residual_impact}
                              level={r.residual_severity} />
                <div className="mt-3 flex items-center gap-2">
                  <select className="h-9 rounded-md border border-border bg-card px-3 text-sm flex-1"
                          value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                    {["open", "mitigating", "accepted", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Button size="sm" onClick={saveResidual} data-testid="residual-save">
                    <Check className="w-4 h-4" /> Save
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Business impact</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {["financial", "reputational", "operational"].map((k) => {
              const level = r.business_impact?.[k] || "—";
              return (
                <div key={k} className="rounded-md border border-border p-4">
                  <div className="label-eyebrow capitalize">{k}</div>
                  <div className={cn("mt-2 inline-flex px-2 py-0.5 rounded-full text-xs font-semibold",
                    level === "high" ? "bg-red-100 text-red-800" :
                    level === "medium" ? "bg-amber-100 text-amber-800" :
                    level === "low" ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground")}>
                    {level}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {r.acceptance && (
        <Card data-testid="risk-acceptance-card">
          <CardHeader><CardTitle>Risk acceptance</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div><span className="text-muted-foreground">Accepted by:</span> {r.acceptance.accepted_by_name}</div>
            <div><span className="text-muted-foreground">On:</span> {r.acceptance.accepted_at && new Date(r.acceptance.accepted_at).toLocaleString()}</div>
            {r.acceptance.expiry_date && <div><span className="text-muted-foreground">Expires:</span> {new Date(r.acceptance.expiry_date).toLocaleDateString()}</div>}
            <div className="pt-2"><span className="text-muted-foreground">Rationale:</span> {r.acceptance.rationale}</div>
          </CardContent>
        </Card>
      )}

      {showAccept && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowAccept(false)}>
          <div className="w-full max-w-lg card-surface animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold">Accept risk</h3>
              <button onClick={() => setShowAccept(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={accept} className="p-6 space-y-4">
              <div>
                <Label>Rationale</Label>
                <Textarea data-testid="accept-rationale" className="mt-1.5" value={acceptForm.rationale}
                          onChange={(e) => setAcceptForm({ ...acceptForm, rationale: e.target.value })}
                          required minLength={3} />
              </div>
              <div>
                <Label>Acceptance expiry (optional)</Label>
                <Input type="date" className="mt-1.5" value={acceptForm.acceptance_expiry}
                       onChange={(e) => setAcceptForm({ ...acceptForm, acceptance_expiry: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAccept(false)}>Cancel</Button>
                <Button type="submit" data-testid="accept-submit">Accept risk</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoreDisplay({ likelihood, impact, score, level }) {
  return (
    <div className="flex items-center gap-4">
      <div className={cn("w-20 h-20 rounded-md flex flex-col items-center justify-center", LEVEL_BG[level])}>
        <div className="text-2xl font-semibold tabular-nums leading-none">{score}</div>
        <div className="text-[10px] uppercase tracking-wider mt-1 opacity-90">score</div>
      </div>
      <div className="text-sm">
        <div>Likelihood: <span className="tabular-nums font-medium">{likelihood}</span> / 5</div>
        <div>Impact: <span className="tabular-nums font-medium">{impact}</span> / 5</div>
        <div className="text-xs text-muted-foreground mt-1 capitalize">{level} risk</div>
      </div>
    </div>
  );
}
